import React from 'react';
import cx from 'classnames';
import {
  LIVE_PROMPT_FILTER_BUTTON_LIST,
  MAIN_FILTER_BUTTONS_LIST,
  POST_FILTER_BUTTON_LIST,
  PROMPT_FILTER_BUTTON_LIST,
  TerritoryActiveMainFilter,
  TerritoryPostActiveFilter,
  TerritoryPromptActiveFilter,
} from './constants';
import { ClearFiltersButton } from './ClearFiltersButton';
import { TerritoryExploreListItem } from './TerritoryExploreListItem';
import { useTerritoryActiveFilter } from './hooks/useTerritoryActiveFilter';
import ProtectWithLogin from '../ProtectWithLogin/ProtectWithLogin';

type FilterItem<T> = {
  label: string;
  value: T;
};

type FilterListProps<T> = {
  filters: FilterItem<T>[];
  activeFilter: T | null | undefined;
  onClick: (value: T) => void;
  isThirdFilterActive?: boolean;
};

const FilterList = <T,>({ filters, activeFilter, onClick, isThirdFilterActive }: FilterListProps<T>) => (
  <>
    {filters
      .filter((filter) => activeFilter === null || activeFilter === undefined || activeFilter === filter.value)
      .map((filter) => {
        const needsBlockWrapper =
          filter.value === TerritoryPostActiveFilter.author ||
          filter.value === TerritoryPromptActiveFilter.author ||
          filter.value === TerritoryPostActiveFilter.status;

        const listItem = (
          <TerritoryExploreListItem
            key={filter.label}
            isMainFilterActive={activeFilter === filter.value}
            isSecondaryFilterActive={activeFilter}
            onClick={() => onClick(filter.value)}
            isThirdFilterActive={isThirdFilterActive}
          >
            {filter.label}
          </TerritoryExploreListItem>
        );

        return needsBlockWrapper ? <ProtectWithLogin key={filter.label}>{listItem}</ProtectWithLogin> : listItem;
      })}
  </>
);

export const TerritoryExploreFilter = () => {
  const { filters, onMainFilterClick, onPostFilterClick, onPromptFilterClick, onLivePromptFilterClick, resetFilters } =
    useTerritoryActiveFilter();

  const { activeLivePromptFilter, activeMainFilter, activePostFilter, activePromptFilter } = filters;

  const renderMainFilters = () =>
    MAIN_FILTER_BUTTONS_LIST.map(
      (filter) =>
        (activeMainFilter === TerritoryActiveMainFilter.all || activeMainFilter === filter.value) && (
          <TerritoryExploreListItem
            key={filter.label}
            isMainFilterActive={activeMainFilter === filter.value}
            onClick={() => onMainFilterClick(filter.value)}
          >
            <div
              className={cx({
                'pr-8': activeMainFilter !== TerritoryActiveMainFilter.all && (activePostFilter || activePromptFilter),
              })}
            >
              {filter.label}
            </div>
          </TerritoryExploreListItem>
        ),
    );

  const renderPostFilters = () =>
    activeMainFilter === TerritoryActiveMainFilter.posts && (
      <FilterList filters={POST_FILTER_BUTTON_LIST} activeFilter={activePostFilter} onClick={onPostFilterClick} />
    );

  const renderPromptFilters = () =>
    activeMainFilter === TerritoryActiveMainFilter.prompts && (
      <>
        <FilterList
          filters={PROMPT_FILTER_BUTTON_LIST}
          activeFilter={activePromptFilter}
          onClick={onPromptFilterClick}
        />
        {activePromptFilter === TerritoryPromptActiveFilter.status && (
          <FilterList
            filters={LIVE_PROMPT_FILTER_BUTTON_LIST}
            activeFilter={activeLivePromptFilter}
            onClick={onLivePromptFilterClick}
            isThirdFilterActive={activePromptFilter === TerritoryPromptActiveFilter.status}
          />
        )}
      </>
    );

  return (
    <div className="flex items-center gap-3 md:gap-4">
      {activeMainFilter !== TerritoryActiveMainFilter.all && <ClearFiltersButton onClick={resetFilters} />}
      <ul className="flex gap-4 overflow-x-auto no-scrollbar relative">
        {renderMainFilters()}
        {renderPostFilters()}
        {renderPromptFilters()}
      </ul>
    </div>
  );
};
